import React from "react";
import { Card, CardContent } from "@/components/ui/card";

interface SegmentCard {
  id: string;
  label: string;
  percent: number;
  customers: number;
  baseColor: string;
  stripeColor: string;
}

const BASE_SEGMENTS: Omit<SegmentCard, "percent" | "customers">[] = [
  {
    id: "new",
    label: "New",
    baseColor: "#ecfdf5", // emerald-50
    stripeColor: "rgba(16,185,129,0.18)", // emerald-500
  },
  {
    id: "vip",
    label: "VIP",
    baseColor: "#eff6ff", // blue-50
    stripeColor: "rgba(59,130,246,0.18)", // blue-500
  },
  {
    id: "loyal",
    label: "Loyal",
    baseColor: "#ecfeff", // cyan-50
    stripeColor: "rgba(34,211,238,0.18)", // cyan-400
  },
  {
    id: "promising",
    label: "Promising",
    baseColor: "#fff7ed", // orange-50
    stripeColor: "rgba(249,115,22,0.18)", // orange-500
  },
  {
    id: "needs-attention",
    label: "Needs Attention",
    baseColor: "#f5f3ff", // violet-50
    stripeColor: "rgba(129,140,248,0.18)", // indigo-400
  },
  {
    id: "at-risk",
    label: "At Risk",
    baseColor: "#fefce8", // yellow-50
    stripeColor: "rgba(250,204,21,0.22)", // yellow-400
  },
  {
    id: "lost",
    label: "Lost",
    baseColor: "#fee2e2", // red-100
    stripeColor: "rgba(248,113,113,0.22)", // red-400
  },
  {
    id: "cant-lose",
    label: "Can't Loose",
    baseColor: "#ecfeff", // cyan-50
    stripeColor: "rgba(45,212,191,0.22)", // teal-400
  },
];

const generateRandomSegments = (): SegmentCard[] => {
  return BASE_SEGMENTS.map((segment, index) => {
    if (segment.id === "new") {
      // Keep New at 100% like in the design
      return {
        ...segment,
        percent: 100,
        customers: 3,
      };
    }

    // Generate some reasonable random percentages and customer counts
    const percent =
      index < 4
        ? Math.floor(Math.random() * 40) // 0–39 for strong segments
        : Math.floor(Math.random() * 25); // 0–24 for weaker segments

    const customers =
      percent === 0 ? 0 : Math.max(1, Math.floor((percent / 100) * 10));

    return {
      ...segment,
      percent,
      customers,
    };
  });
};

export function SegmentationCustomer() {
  const segments = React.useMemo(generateRandomSegments, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-800">
          Customer Segmentation
        </h2>
        <p className="text-sm text-muted-foreground">
          Distribution of customers across lifecycle segments.
        </p>
      </div>

      <div className="grid gap-4 xl:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {segments.map((segment) => (
          <Card
            key={segment.id}
            className="border-0 shadow-none overflow-hidden"
          >
            <CardContent
              className="p-5 sm:p-6 h-full flex flex-col justify-between"
              style={{
                backgroundColor: segment.baseColor,
                backgroundImage:
                  `repeating-linear-gradient(135deg, ${segment.stripeColor} 0, ${segment.stripeColor} 2px, transparent 2px, transparent 6px)`,
              }}
            >
              <div className="space-y-6 sm:space-y-8">
                <div className="text-sm font-medium text-gray-700">
                  {segment.label}
                </div>

                <div className="space-y-1 sm:space-y-2">
                  <div className="text-4xl sm:text-5xl font-semibold text-gray-900">
                    {segment.percent}
                    <span className="text-2xl align-top ml-1">%</span>
                  </div>
                  <div className="text-sm font-medium text-gray-800">
                    {segment.customers}{" "}
                    <span className="font-normal text-gray-700">
                      {segment.customers === 1 ? "customer" : "customers"}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
